/**
 * Worker pool for heavy synchronous work (classification, geoprocessing, import parsing).
 *
 *   const breaks = await run('classify', { values, nClasses: 5, method: 'jenks' },
 *                            { label: 'Classify layer', signal, transfer: [values.buffer] });
 *
 * - Pool of min(4, hardwareConcurrency || 2) workers over one script (lib/geo.worker.js),
 *   spawned lazily; tasks are defined in lib/geo-tasks.js.
 * - Protocol: `{ id, task, payload }` -> `{ id, type: 'progress'|'result'|'error', ... }`.
 * - Abort (AbortSignal or the TaskConsole cancel button) terminates the worker; a fresh one
 *   is spawned on demand. The promise rejects with an Error named 'AbortError'.
 * - Fallback: if `Worker` is undefined (Jest, SSR, old browsers) or the worker script fails
 *   to load, the same task runs on the main thread through the same dispatcher, so
 *   behaviour is identical (only responsiveness differs).
 * - Passing `label` registers the run in lib/task-store.js so TaskConsole shows progress
 *   and a cancel button.
 */
import { startTask } from './task-store';

export const DEFAULT_POOL_SIZE = Math.min(
  4,
  (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2,
);

/** Classification inputs larger than this go through the pool (smaller ones are cheap). */
export const CLASSIFY_WORKER_THRESHOLD = 20000;
/** Import text longer than this (characters) is parsed in the pool. */
export const PARSE_WORKER_MIN_CHARS = 262144;

function abortError() {
  const e = new Error('Task cancelled');
  e.name = 'AbortError';
  return e;
}
export const isAbortError = (e) => !!e && e.name === 'AbortError';

async function defaultCreateWorker() {
  if (typeof Worker === 'undefined') return null;
  const { createGeoWorker } = await import('./worker-factory');
  return createGeoWorker();
}

async function defaultInline(task, payload, emit) {
  const { runTask } = await import('./geo-tasks');
  return runTask(task, payload, emit);
}

/**
 * Build a pool. `createWorker` (may be async, may return null for "no workers") and
 * `inline` (main-thread executor) are injectable for tests.
 */
export function createPool({ size = DEFAULT_POOL_SIZE, createWorker = defaultCreateWorker, inline = defaultInline } = {}) {
  let nextId = 1;
  let fallback = false;
  const slots = [];  // { worker, job, healthy, dead, starting }
  const queue = [];  // jobs waiting for a free slot

  function settle(job, fn, value) {
    if (job.settled) return;
    job.settled = true;
    if (job.signal && job.onAbort) job.signal.removeEventListener('abort', job.onAbort);
    fn(value);
  }

  function spawn(slot) {
    slot.starting = Promise.resolve()
      .then(() => createWorker())
      .catch(() => null)
      .then((w) => {
        if (slot.dead) { if (w && w.terminate) w.terminate(); return; }
        if (!w) { enableFallback(); return; }
        w.onmessage = (e) => onMessage(slot, e.data);
        w.onerror = (e) => onError(slot, e);
        slot.worker = w;
        if (slot.job) post(slot);
      });
  }

  function post(slot) {
    const { job } = slot;
    slot.worker.postMessage({ id: job.id, task: job.task, payload: job.payload }, job.transfer || []);
  }

  function retire(slot) {
    slot.dead = true;
    const i = slots.indexOf(slot);
    if (i !== -1) slots.splice(i, 1);
    try { slot.worker && slot.worker.terminate(); } catch { /* ignore */ }
    slot.worker = null;
  }

  function enableFallback() {
    if (fallback) return;
    fallback = true;
    const orphaned = slots.map(s => s.job).filter(Boolean);
    [...slots].forEach(retire);
    orphaned.forEach(runInline);
    while (queue.length) runInline(queue.shift());
  }

  function onMessage(slot, msg) {
    slot.healthy = true;
    const job = slot.job;
    if (!job || !msg || msg.id !== job.id) return;
    if (msg.type === 'progress') {
      if (job.onProgress) job.onProgress(msg.value, msg.message);
      return;
    }
    slot.job = null;
    if (msg.type === 'result') {
      settle(job, job.resolve, msg.result);
    } else {
      const err = new Error(msg.error?.message || 'Worker task failed');
      if (msg.error?.name) err.name = msg.error.name;
      settle(job, job.reject, err);
    }
    next(slot);
  }

  function onError(slot, e) {
    if (e && e.preventDefault) e.preventDefault();
    const job = slot.job;
    if (!slot.healthy) {
      // Never answered: the script itself failed to load or start. Use the main thread.
      enableFallback();
      return;
    }
    retire(slot);
    if (job) settle(job, job.reject, new Error((e && e.message) || 'Worker crashed'));
    pump();
  }

  function next(slot) {
    if (queue.length) {
      slot.job = queue.shift();
      post(slot);
    }
  }

  function pump() {
    while (queue.length) {
      const free = slots.find(s => !s.job);
      if (free) {
        free.job = queue.shift();
        if (free.worker) post(free);
      } else if (slots.length < size) {
        const slot = { worker: null, job: queue.shift(), healthy: false, dead: false };
        slots.push(slot);
        spawn(slot);
      } else {
        break;
      }
    }
  }

  async function runInline(job) {
    if (job.settled) return;
    job.inline = true;
    try {
      const result = await inline(job.task, job.payload, (value, message) => {
        if (job.settled) throw abortError();
        if (job.onProgress) job.onProgress(value, message);
      });
      settle(job, job.resolve, result);
    } catch (err) {
      settle(job, job.reject, err);
    }
  }

  function enqueue(job) {
    if (fallback) { runInline(job); return; }
    queue.push(job);
    pump();
  }

  function cancel(job) {
    if (job.settled) return;
    const qi = queue.indexOf(job);
    if (qi !== -1) queue.splice(qi, 1);
    const slot = slots.find(s => s.job === job);
    settle(job, job.reject, abortError());
    if (slot) { retire(slot); pump(); } // kill the worker; a fresh one spawns on demand
  }

  /**
   * @param {string} task - name from lib/geo-tasks.js
   * @param {object} payload - structured-clone friendly
   * @param {{ onProgress?: (fraction:number, message?:string)=>void, signal?: AbortSignal,
   *           transfer?: Transferable[], label?: string, doneMessage?: (result:any)=>string }} [opts]
   * @returns {Promise<any>}
   */
  function run(task, payload, opts = {}) {
    const { onProgress, signal, transfer, label, doneMessage } = opts;
    return new Promise((resolve, reject) => {
      if (signal && signal.aborted) { reject(abortError()); return; }

      let handle = null;
      const job = { id: nextId++, task, payload, transfer, signal, settled: false };
      if (label) handle = startTask(label, { cancel: () => cancel(job) });
      job.onProgress = (v, m) => {
        if (handle) handle.update(v, m);
        if (onProgress) onProgress(v, m);
      };
      job.resolve = (r) => {
        if (handle) handle.done(doneMessage ? doneMessage(r) : undefined);
        resolve(r);
      };
      job.reject = (e) => {
        if (handle) { if (isAbortError(e)) handle.cancelled(); else handle.fail(e?.message || 'failed'); }
        reject(e);
      };
      if (signal) {
        job.onAbort = () => cancel(job);
        signal.addEventListener('abort', job.onAbort);
      }
      enqueue(job);
    });
  }

  /** Terminate every worker and reject queued/running jobs (used by tests and HMR). */
  function shutdown() {
    [...queue].forEach(cancel);
    slots.map(s => s.job).filter(Boolean).forEach(cancel);
    [...slots].forEach(retire);
  }

  return {
    run,
    shutdown,
    get size() { return size; },
    get workerCount() { return slots.length; },
    get usingFallback() { return fallback; },
  };
}

/** App-wide pool. */
export const pool = createPool();

/** Shorthand for `pool.run(...)`. */
export const run = (task, payload, opts) => pool.run(task, payload, opts);
